import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { formatNOK } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { RevisionDiffSummary } from "@/lib/budget-revision";
import type { RevisionAssignmentAdjustment } from "@/lib/budget-revision-assignments";

/**
 * VIB-1202: siste steg i revisjonen — krev begrunnelse, vis diff-oppsummering
 * (linjeendringer, total- og BF-effekt inkl. eksplisitt provisjonsprognose-
 * linje), og commit. Ingen re-godkjenningsrunde (Erik 15.07: begrunnelse +
 * logg er nok).
 */
export function BudgetRevisionCommitDialog({
  open,
  onOpenChange,
  summary,
  assignmentAdjustments,
  totalBefore,
  totalAfter,
  bfBefore,
  bfAfter,
  isPending,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  summary: RevisionDiffSummary;
  assignmentAdjustments: RevisionAssignmentAdjustment[];
  totalBefore: number;
  totalAfter: number;
  bfBefore: number;
  bfAfter: number;
  isPending: boolean;
  onConfirm: (reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  const nChanges = summary.updatedCount + summary.createdCount + summary.deletedCount;
  const canConfirm = reason.trim().length > 0 && nChanges > 0 && !isPending;
  const changesCompletedAssignments = assignmentAdjustments.some(
    (adjustment) =>
      adjustment.taskStatus === "completed" || adjustment.taskStatus === "approved",
  );

  const Row = ({ label, before, after }: { label: string; before: number; after: number }) => (
    <div className="flex items-center justify-between text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="tabular-nums">
        {formatNOK(before)}{" "}
        <span className="text-muted-foreground">→</span>{" "}
        <span className={cn(before !== after && "font-semibold")}>{formatNOK(after)}</span>
      </span>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Fullfør budsjettrevisjon</DialogTitle>
          <DialogDescription>
            Kontroller økonomiske konsekvenser og endringer i tildelte timer før du lagrer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="rounded-md border border-border p-3 space-y-1.5">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {summary.updatedCount} endret · {summary.createdCount} ny · {summary.deletedCount} fjernet
            </p>
            <Row label="Tilbudssum" before={totalBefore} after={totalAfter} />
            <Row label="GEEK estimert" before={bfBefore} after={bfAfter} />
            {bfBefore !== bfAfter && (
              <p className="text-[11px] text-amber-700 pt-1">
                Merk: provisjonsprognosen (GEEK estimert) på aktive prosjekter endres fra{" "}
                {formatNOK(bfBefore)} til {formatNOK(bfAfter)}.
              </p>
            )}
          </div>

          {assignmentAdjustments.length > 0 && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-2">
              <p className="text-sm font-medium text-amber-900">
                Tildelte timer justeres automatisk
              </p>
              <p className="text-xs text-amber-800">
                Reduksjonen fordeles proporsjonalt mellom de som er tildelt oppgaven.
                Avbryt og juster tildelingen først hvis fordelingen skal være annerledes.
              </p>
              {changesCompletedAssignments && (
                <p className="text-xs font-medium text-amber-900">
                  Fullførte eller godkjente tildelinger endres. Dette påvirker
                  faktureringsgraden i perioden oppgavene ble fullført.
                </p>
              )}
              <div className="space-y-1.5">
                {assignmentAdjustments.map((adjustment) => (
                  <div
                    key={adjustment.assignmentId}
                    className="flex items-start justify-between gap-4 text-xs"
                  >
                    <span>
                      <span className="font-medium">{adjustment.userName}</span>
                      <span className="text-muted-foreground"> · {adjustment.lineDescription}</span>
                    </span>
                    <span className="shrink-0 tabular-nums">
                      {adjustment.beforeHours}t → {adjustment.afterHours}t
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium">Begrunnelse (påkrevd)</label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Hvorfor revideres budsjettet? (f.eks. «Kunden økte omfang på post-produksjon»)"
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
            Avbryt
          </Button>
          <Button onClick={() => onConfirm(reason.trim())} disabled={!canConfirm}>
            {isPending ? "Lagrer…" : "Bekreft revisjon"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
